import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ContactExportFilters,
  downloadContactsExportCsv,
  ExportSessionProgress,
  getExportSession,
  getQueueHealth,
  QueueHealth,
  startContactsExport,
} from "../../helpers/audience_helper";

const POLL_MS = 2000;
const STUCK_HINT_MS = 12000;

type Props = {
  isOpen: boolean;
  /** Filtros do POST — ignorados se `resumeSessionId` estiver setado. */
  filters: ContactExportFilters;
  /** Retoma polling de uma sessão existente (histórico). */
  resumeSessionId?: number | null;
  onClose: () => void;
  /** Após completed/failed — lista pode recarregar o histórico. */
  onSettled?: () => void;
};

function buildDownloadFilename(progress: ExportSessionProgress): string {
  const date =
    progress.updated_at?.slice(0, 10) ||
    progress.created_at?.slice(0, 10) ||
    new Date().toISOString().slice(0, 10);
  const rows = Math.max(0, progress.total_rows ?? 0);
  return `contatos_${date}_${rows}linhas.csv`;
}

/**
 * Modal de export CSV — 202 + polling + download blob (DOC 09 / 13 · LAC-027).
 * Auto-download 1× quando ready; resume via histórico.
 */
const ContactExportModal = ({
  isOpen,
  filters,
  resumeSessionId = null,
  onClose,
  onSettled,
}: Props) => {
  const { t } = useTranslation();
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [progress, setProgress] = useState<ExportSessionProgress | null>(null);
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showStuckHint, setShowStuckHint] = useState(false);
  const [queueHealth, setQueueHealth] = useState<QueueHealth | null>(null);
  const [apiQueuedMessage, setApiQueuedMessage] = useState<string | null>(null);
  const startedRef = useRef(false);
  const toastedRef = useRef(false);
  const autoDownloadedRef = useRef(false);
  const settledRef = useRef(false);

  const status = progress?.status;
  const isTerminal =
    status === "completed" || status === "failed" || status === "Failed";
  const isPolling = sessionId !== null && !isTerminal && !error;

  const filterChips = useMemo(() => {
    const source = progress?.filters ?? filters;
    const chips: string[] = [];
    if (source.search) {
      chips.push(t("contacts.export.filter.search", { q: source.search }));
    }
    if (source.phone) {
      chips.push(t("contacts.export.filter.phone", { phone: source.phone }));
    }
    if (source.status) {
      chips.push(
        t("contacts.export.filter.status", {
          status: t(`contacts.status.${source.status}`, {
            defaultValue: source.status,
          }),
        })
      );
    }
    if (source.trashed === "only") {
      chips.push(t("contacts.export.filter.trashed"));
    }
    if (source.tag_ids && source.tag_ids.length > 0) {
      chips.push(
        t("contacts.export.filter.tags", {
          count: source.tag_ids.length,
          match:
            source.tag_match === "and"
              ? t("contacts.filter.match.and")
              : t("contacts.filter.match.or"),
        })
      );
    }
    if (source.fields && Object.keys(source.fields).length > 0) {
      chips.push(
        t("contacts.export.filter.fields", {
          count: Object.keys(source.fields).length,
        })
      );
    }
    return chips;
  }, [filters, progress?.filters, t]);

  const reset = useCallback(() => {
    setSessionId(null);
    setProgress(null);
    setStarting(false);
    setDownloading(false);
    setError(null);
    setShowStuckHint(false);
    setApiQueuedMessage(null);
    startedRef.current = false;
    toastedRef.current = false;
    autoDownloadedRef.current = false;
    settledRef.current = false;
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  // Uma sessão por abertura — POST novo ou resume do histórico.
  useEffect(() => {
    if (!isOpen || startedRef.current) {
      return;
    }
    startedRef.current = true;

    if (resumeSessionId) {
      setSessionId(resumeSessionId);
      return;
    }

    setStarting(true);
    setError(null);

    void (async () => {
      try {
        const res = await startContactsExport(filters);
        if (!res?.success || !res.data?.id) {
          setError(t("contacts.export.error.start"));
          return;
        }
        setApiQueuedMessage(res.message ?? null);
        setSessionId(res.data.id);
        toast.info(res.message || t("contacts.export.toast.queued"), {
          autoClose: 3000,
        });
      } catch (err: any) {
        setError(err?.message || t("contacts.export.error.start"));
      } finally {
        setStarting(false);
      }
    })();
  }, [isOpen, filters, resumeSessionId, t]);

  const pollOnce = useCallback(
    async (id: number) => {
      try {
        const res = await getExportSession(id);
        if (!res?.success || !res.data) {
          setError(t("contacts.export.error.progress"));
          return;
        }
        setProgress(res.data);
      } catch (err: any) {
        setError(err?.message || t("contacts.export.error.progress"));
      }
    },
    [t]
  );

  useEffect(() => {
    if (!sessionId || isTerminal) {
      return;
    }

    void pollOnce(sessionId);
    const timer = window.setInterval(() => {
      void pollOnce(sessionId);
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [sessionId, isTerminal, pollOnce]);

  useEffect(() => {
    if (!isPolling || status !== "pending") {
      setShowStuckHint(false);
      setQueueHealth(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setShowStuckHint(true);
      void getQueueHealth()
        .then((res) => {
          if (res?.success && res.data) {
            setQueueHealth(res.data);
          }
        })
        .catch(() => setQueueHealth(null));
    }, STUCK_HINT_MS);
    return () => window.clearTimeout(timer);
  }, [isPolling, status, sessionId]);

  const runDownload = useCallback(
    async (silent = false) => {
      if (!sessionId || !progress?.download_ready) {
        return;
      }
      setDownloading(true);
      try {
        await downloadContactsExportCsv(
          sessionId,
          buildDownloadFilename(progress)
        );
        if (!silent) {
          toast.success(t("contacts.export.toast.downloaded"), {
            autoClose: 2500,
          });
        }
      } catch (err: any) {
        toast.error(err?.message || t("contacts.export.error.download"), {
          autoClose: 4500,
        });
      } finally {
        setDownloading(false);
      }
    },
    [sessionId, progress, t]
  );

  useEffect(() => {
    if (!progress || toastedRef.current) {
      return;
    }
    if (progress.status === "completed") {
      toastedRef.current = true;
      toast.success(t("contacts.export.toast.ready"), { autoClose: 3500 });
      if (!settledRef.current) {
        settledRef.current = true;
        onSettled?.();
      }
    }
    if (progress.status === "failed") {
      toastedRef.current = true;
      toast.error(
        progress.failure_message || t("contacts.export.toast.failed"),
        { autoClose: 5000 }
      );
      if (!settledRef.current) {
        settledRef.current = true;
        onSettled?.();
      }
    }
  }, [progress, t, onSettled]);

  // Auto-download 1× quando o arquivo fica pronto (Carla não precisa clicar de novo).
  useEffect(() => {
    if (
      !progress?.download_ready ||
      autoDownloadedRef.current ||
      !sessionId ||
      (progress.total_rows ?? 0) === 0
    ) {
      return;
    }
    autoDownloadedRef.current = true;
    void runDownload(true);
  }, [progress, sessionId, runDownload]);

  const percent = progress?.progress_percent ?? 0;
  const showZeroRows =
    progress?.status === "completed" && (progress.total_rows ?? 0) === 0;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {t("contacts.export.modalTitle")}
      </ModalHeader>
      <ModalBody>
        {starting && (
          <div className="text-center py-3">
            <Spinner color="primary" className="mb-2" />
            <p className="text-muted mb-0">{t("contacts.export.starting")}</p>
          </div>
        )}

        {error && (
          <Alert color="danger" className="mb-0">
            {error}
          </Alert>
        )}

        {!starting && !error && sessionId !== null && (
          <>
            {filterChips.length > 0 ? (
              <div className="mb-3">
                <p className="text-muted small mb-1">
                  {t("contacts.export.filtersTitle")}
                </p>
                <div className="d-flex flex-wrap gap-1">
                  {filterChips.map((chip) => (
                    <Badge key={chip} color="light" className="text-dark border">
                      {chip}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-muted small mb-3">
                {t("contacts.export.filtersAll")}
              </p>
            )}

            {apiQueuedMessage && status === "pending" && (
              <p className="text-muted small">{apiQueuedMessage}</p>
            )}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-medium">
                {t(`contacts.export.status.${status || "pending"}`, {
                  defaultValue: t("contacts.export.status.pending"),
                })}
              </span>
              {isPolling && <Spinner size="sm" color="primary" />}
            </div>
            <Progress
              value={
                progress?.status === "completed" && percent === 0 ? 100 : percent
              }
              className="progress-lg"
              animated={isPolling}
              color={
                status === "failed"
                  ? "danger"
                  : status === "completed"
                    ? "success"
                    : "primary"
              }
            >
              {(percent > 0 || status === "completed") &&
                `${status === "completed" && percent === 0 ? 100 : percent}%`}
            </Progress>
            <p className="text-muted small mt-2 mb-0">
              {t("contacts.export.progressDetail", {
                processed: progress?.processed_rows ?? 0,
                total: progress?.total_rows ?? 0,
              })}
            </p>

            {showStuckHint && (
              <Alert
                color={
                  queueHealth?.worker_ok === false ? "danger" : "warning"
                }
                className="mt-3 mb-0"
              >
                {queueHealth?.worker_ok === false
                  ? t("contacts.export.stuckHintWorkerDown")
                  : queueHealth?.worker_ok === true
                    ? t("contacts.export.stuckHintWorkerOk")
                    : t("contacts.export.stuckHint")}
              </Alert>
            )}

            {showZeroRows && (
              <Alert color="info" className="mt-3 mb-0">
                {t("contacts.export.zeroRows")}
              </Alert>
            )}

            {status === "failed" && (
              <Alert color="danger" className="mt-3 mb-0">
                {progress?.failure_message || t("contacts.export.toast.failed")}
              </Alert>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {progress?.download_ready && (
          <Button
            color="primary"
            onClick={() => void runDownload(false)}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <Spinner size="sm" className="me-1" />
                {t("contacts.export.downloading")}
              </>
            ) : (
              <>
                <i className="ri-download-2-line me-1" aria-hidden="true" />
                {t("contacts.export.download")}
              </>
            )}
          </Button>
        )}
        <Button color="light" onClick={handleClose}>
          {progress?.download_ready || status === "failed" || error
            ? t("contacts.export.close")
            : t("contacts.export.keepBackground")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ContactExportModal;
